import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { merge, Observable, of, take } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { filterDefined } from '@core/common-legacy';
import { fetchIfNotLoaded } from '@core/ngrx-legacy';
import {
  CompanyVerificationActions,
  CompanyVerificationSelectors,
} from '@core/tellent-company-verification-data';
import {
  CompanyVerificationRequestPayload,
  CompanyVerificationRequestStatus,
  CompanyVerificationResult,
} from '@core/tellent-company-verification-types';
import { TellentCurrentUserSelectors } from '@core/tellent-data';

@Injectable({ providedIn: 'root' })
export class CompanyVerificationDataService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  private readonly updateSuccess = this.actions$.pipe(
    ofType(CompanyVerificationActions.updateSuccess),
  );
  private readonly updateFailed = this.actions$.pipe(
    ofType(CompanyVerificationActions.updateFailed),
    map(action => {
      const errors = action.payload.error?.error?.errors;
      return Array.isArray(errors) ? errors : [];
    }),
  );

  private readonly data$ = this.store.pipe(
    this.fetchIfNotLoaded(),
    select(CompanyVerificationSelectors.data),
    filterDefined,
  );

  public status$ = this.store
    .select(TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe)
    .pipe(
      map(me => me?.org?.signupDomainVerified),
      switchMap(verified => {
        if (verified) return of(CompanyVerificationRequestStatus.Approved);
        return this.data$.pipe(map(data => data.status));
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );

  public verify(data: CompanyVerificationRequestPayload): Observable<CompanyVerificationResult> {
    this.store.dispatch(CompanyVerificationActions.update({ payload: data }));

    return merge(
      this.updateSuccess.pipe(
        map(() => ({
          success: true,
        })),
      ),
      this.updateFailed.pipe(
        map(errors => ({
          success: false,
          errors,
        })),
      ),
    ).pipe(take(1));
  }

  private fetchIfNotLoaded() {
    return fetchIfNotLoaded(this.store.select(CompanyVerificationSelectors.meta), () =>
      this.store.dispatch(CompanyVerificationActions.fetch()),
    );
  }
}
